import React from 'react';
import { RESORT_GRID } from 'Cms/constants';
import type { CMSProps, GQLProps, GQLData } from 'Cms/products-grid/resolver';
import type { ResortGridProps } from 'Components/widgets/products-grid/ProductsGrid';
import { useAppContext } from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { LineTitle } from 'Components/widgets/line-title/LineTitle';
import { ProductsGrid } from 'Components/widgets/products-grid/ProductsGrid';
import { FacetSearch } from 'Components/widgets/facet-search/FacetSearch';
import { asWidgetProps } from 'Src/types/cast-helpers';
import type { ComponentProps } from 'react';
import { productsMapper } from './ProductsGridLine.utils';
type Props = CMSProps &
  GQLProps &
  GQLData & {
    id?: string;
    title?: string;
  };
export const ProductsGridLine = ({ id, ...props }: Props) => {
  const {
    config,
    departureCity,
    departureDateEnd,
    departureDateStart,
    displayActivityFilter = true,
    displayChildcareFilter = true,
    displayDestinationFilter = true,
    displaySeasonFilter = true,
    displayTypeOfStayFilter = true,
    displayFlightDurationFilter = true,
    displayPrices,
    displaySearch,
    duration,
    moreResultsColor,
    moreResultsLabel,
    moreResultsButtonLabel,
    offer,
    productIds = [],
    sortByPriceAscending,
    title,
    unfoldList,
  } = props;
  const appContext = useAppContext();
  const isProductComparisonModeAuthorized = isFeatureFlipped(appContext)(
    'search.productComparison',
  );
  const products = productsMapper(props);
  const seeMore = {
    color: moreResultsColor,
    text: moreResultsLabel,
    label: moreResultsButtonLabel,
  };
  const resortGridProps: ResortGridProps = {
    departureCity,
    departureDateEnd,
    departureDateStart,
    displayPrices,
    duration,
  };
  const orderByPrice = sortByPriceAscending ? 'asc' : undefined;
  return displaySearch ? (
    <div className="Line Line-ProductsGrid" id={id}>
      {!!title && (
        <SiteContainer>
          <LineTitle>{title}</LineTitle>
        </SiteContainer>
      )}
      <FacetSearch
        displayActivityFilter={displayActivityFilter}
        displayChildcareFilter={displayChildcareFilter}
        displayDestinationFilter={displayDestinationFilter}
        displaySeasonFilter={displaySeasonFilter}
        displayTypeOfStayFilter={displayTypeOfStayFilter}
        displayFlightDurationFilter={displayFlightDurationFilter}
        hideSearchInput
        i18n={config}
        initialIsSearchEnabled
        initialProducts={products || []}
        isProductComparisonModeAuthorized={isProductComparisonModeAuthorized}
        isSynchronousGrid
        resultsHasSeeMore={!unfoldList}
        resultsSeeMore={seeMore}
        offer={offer}
        orderByPrice={orderByPrice}
        resetSearchAtMount
        resortGridProps={resortGridProps}
        shouldPersistCriteriaInUrl
        shouldSetDefaultDepartureCityOnCompare
        withSearchFacets
        withSearchForm
        withSearchResults
      />
    </div>
  ) : (
    <SiteContainer className="Line Line-ProductsGrid" id={id}>
      <LineTitle>{title}</LineTitle>
      <ProductsGrid
        {...asWidgetProps<ComponentProps<typeof ProductsGrid>>({
          ...props,
          hasSeeMore: !unfoldList,
          isSynchronousGrid: true,
          lineName: RESORT_GRID,
          products,
          productsMapper,
          orderByPrice,
          seeMore,
          totalNumberToDisplay: productIds.length,
        })}
      />
    </SiteContainer>
  );
};
